import React, { useEffect } from 'react';
import {
  ADD_SMART_ORDER_ITEM_MUTATION,
  UPDATE_INBOUND_QUANTITY_MUTATION,
} from './queries';
import { InboundReducerAction } from '@/hooks/use-order-inbound-reducer';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import {
  OrderInbound_AddSmartOrderItemMutation,
  OrderInbound_AddSmartOrderItemMutationVariables,
  OrderInbound_UpdateQuantityMutation,
  OrderInbound_UpdateQuantityMutationVariables,
} from '@/types';
import { QuantitySelector } from '@/components/quantity-selector';
import { useDebouncedCallback } from 'use-debounce';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useMutation } from '@apollo/client';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  dispatch: React.Dispatch<InboundReducerAction>;
  item: ItemListOptionMetadata;
  smartOrderUUID: string;
}

export const InboundRenderAddActions: React.FC<Props> = ({
  item,
  smartOrderUUID,
  dispatch,
}) => {
  const { isViewOnly } = useOrderInbounds();
  const itemQueryVariables = useItemQueryVariables();
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useUserContext();
  const pendingQuantityMap = useOrderInboundDetailStore(
    (state) => state.pendingQuantityMap,
  );
  const updatePendingItem = useOrderInboundDetailStore(
    (state) => state.actions.updatePendingItem,
  );

  const variantPackUUID = item.variantPackUUID;
  const pendingItemDetails = pendingQuantityMap.get(variantPackUUID);
  const pendingSearchInputUUID = pendingItemDetails?.searchInputUUID;
  const isConfirmed = pendingItemDetails?.confirmed;

  // Only update search input if the item is confirmed and has a savedsearch input
  const shouldUpdateSearchInput = isConfirmed && pendingSearchInputUUID;

  const [addSmartOrderItemInline, { loading: isAddingItem }] = useMutation<
    OrderInbound_AddSmartOrderItemMutation,
    OrderInbound_AddSmartOrderItemMutationVariables
  >(ADD_SMART_ORDER_ITEM_MUTATION);

  const [updateQuantityInline] = useMutation<
    OrderInbound_UpdateQuantityMutation,
    OrderInbound_UpdateQuantityMutationVariables
  >(UPDATE_INBOUND_QUANTITY_MUTATION);

  // Create new search input and add to reducer state
  const makeAddItemMutation = async (
    newQuantity: number,
    clientSearchInputUUID: string,
  ) => {
    try {
      const result = await addSmartOrderItemInline({
        variables: {
          ...itemQueryVariables,
          quantity: newQuantity,
          restaurantUUID: selectedRestaurantUUID,
          searchTerm: item.displayName,
          smartOrderUUID,
          supplierUUID,
          variantPackUUID,
        },
      });
      const searchInput =
        result.data?.addSmartOrderItem?.smart_order_search_input;
      if (searchInput) {
        updatePendingItem(variantPackUUID, {
          confirmed: true,
          quantity: newQuantity,
          searchInputUUID: searchInput?.uuid,
        });
        dispatch({
          payload: {
            clientSearchInputUUID,
            searchInput,
          },
          type: 'CONFIRM_ADD_ITEM',
        });
      } else {
        throw new Error('No search input returned from mutation');
      }
    } catch (error) {
      showErrorToast();
    }
  };

  const debouncedMakeAddItemMutation = useDebouncedCallback(
    makeAddItemMutation,
    1000,
  );

  const makeUpdateQuantityMutation = async (newQuantity: number) => {
    if (!pendingSearchInputUUID) {
      return;
    }
    try {
      await updateQuantityInline({
        variables: {
          ...itemQueryVariables,
          quantity: newQuantity,
          restaurantUUID: selectedRestaurantUUID,
          smartOrderInputUUID: pendingSearchInputUUID,
        },
      });
    } catch (error) {
      showErrorToast();
    }
  };

  const debouncedMakeUpdateQuantityMutation = useDebouncedCallback(
    makeUpdateQuantityMutation,
    1000,
  );

  useEffect(() => {
    return () => {
      // Flush any pending mutations before unmounting
      debouncedMakeAddItemMutation.flush();
      debouncedMakeUpdateQuantityMutation.flush();
    };
  }, [debouncedMakeAddItemMutation, debouncedMakeUpdateQuantityMutation]);

  const handleUpdateSearchInput = async (newQuantity: number) => {
    if (!pendingSearchInputUUID) {
      return;
    }
    updatePendingItem(variantPackUUID, {
      quantity: newQuantity,
      searchInputUUID: pendingSearchInputUUID,
    });
    dispatch({
      payload: {
        quantity: newQuantity,
        searchInputUUID: pendingSearchInputUUID,
      },
      type: 'UPDATE_QUANTITY',
    });
    debouncedMakeUpdateQuantityMutation(newQuantity);
  };

  const handleAddOrRetry = (newQuantity: number) => {
    const clientSearchInputUUID = pendingSearchInputUUID ?? uuidv4();

    if (pendingSearchInputUUID) {
      dispatch({
        payload: {
          quantity: newQuantity,
          searchInputUUID: pendingSearchInputUUID,
        },
        type: 'UPDATE_QUANTITY',
      });
    } else {
      dispatch({
        payload: {
          itemToAdd: item,
          quantity: newQuantity,
          searchInputUUID: clientSearchInputUUID,
        },
        type: 'ADD_ITEM',
      });
    }

    updatePendingItem(variantPackUUID, {
      confirmed: false,
      quantity: newQuantity,
      searchInputUUID: clientSearchInputUUID,
    });

    if (newQuantity === 0) {
      debouncedMakeAddItemMutation.cancel();
    } else {
      debouncedMakeAddItemMutation(newQuantity, clientSearchInputUUID);
    }
  };

  if (isViewOnly) {
    return null;
  }

  return (
    <QuantitySelector
      canDecrementToZero
      isFocused={false}
      itemDisplayName={item.displayName}
      loading={isAddingItem}
      maxQuantity={item.maximumQuantity}
      minQuantity={item.minimumQuantity}
      onChange={(_: number, newQuantity: number) => {
        // only allow one add item request to prevent duplicate search inputs
        if (isAddingItem) {
          return;
        }
        if (shouldUpdateSearchInput) {
          handleUpdateSearchInput(newQuantity);
        } else {
          handleAddOrRetry(newQuantity);
        }
      }}
      quantity={pendingItemDetails?.quantity ?? 0}
    />
  );
};
